import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Tag, Calendar, DollarSign, Percent, Edit2, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { toast } from 'sonner';
import { GiftCardPromotionService, GiftCardPromotion } from '../services/giftCardPromotionService';

interface GiftCardPromotionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function GiftCardPromotionsModal({ isOpen, onClose }: GiftCardPromotionsModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [promotions, setPromotions] = useState<GiftCardPromotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<GiftCardPromotion | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    bonus_type: 'percentage' as 'fixed' | 'percentage' | 'incremental',
    bonus_value: '',
    threshold_amount: '',
    start_date: '',
    end_date: '',
    is_active: true
  });

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadPromotions();
    }
  }, [isOpen, selectedBusiness?.id]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setEditingPromotion(null);
      setIsCreating(false);
    }
  }, [isOpen]);

  const loadPromotions = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await GiftCardPromotionService.getPromotions(selectedBusiness.id);
      setPromotions(data);
    } catch (error) {
      console.error('Error loading promotions:', error);
      toast.error('Failed to load promotions');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      bonus_type: 'percentage',
      bonus_value: '',
      threshold_amount: '',
      start_date: '',
      end_date: '',
      is_active: true
    });
  };

  const handleClose = () => {
    onClose();
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedBusiness?.id) return;

    const bonusValue = parseFloat(formData.bonus_value);
    if (isNaN(bonusValue) || bonusValue <= 0) {
      toast.error('Bonus value must be greater than 0');
      return;
    }

    if (!formData.start_date || !formData.end_date) {
      toast.error('Please select start and end dates');
      return;
    }

    if (new Date(formData.end_date) <= new Date(formData.start_date)) {
      toast.error('End date must be after start date');
      return;
    }

    // Validate threshold amount for incremental bonus
    if (formData.bonus_type === 'incremental') {
      const thresholdAmount = parseFloat(formData.threshold_amount);
      if (isNaN(thresholdAmount) || thresholdAmount <= 0) {
        toast.error('Threshold amount must be greater than 0 for incremental bonus');
        return;
      }
    }

    try {
      const promotionData = {
        name: formData.name,
        description: formData.description || undefined,
        bonus_type: formData.bonus_type,
        bonus_value: bonusValue,
        threshold_amount: formData.bonus_type === 'incremental' 
          ? parseFloat(formData.threshold_amount)
          : undefined,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        is_active: formData.is_active
      };

      if (editingPromotion) {
        await GiftCardPromotionService.updatePromotion(editingPromotion.id, promotionData);
        toast.success('Promotion updated successfully');
      } else {
        await GiftCardPromotionService.createPromotion({
          business_id: selectedBusiness.id,
          ...promotionData
        });
        toast.success('Promotion created successfully');
      }

      resetForm();
      setIsCreating(false);
      setEditingPromotion(null);
      loadPromotions();
    } catch (error) {
      console.error('Error saving promotion:', error);
      toast.error('Failed to save promotion');
    }
  };

  const handleEdit = (promotion: GiftCardPromotion) => {
    setEditingPromotion(promotion);
    setFormData({
      name: promotion.name,
      description: promotion.description || '',
      bonus_type: promotion.bonus_type,
      bonus_value: promotion.bonus_value.toString(),
      threshold_amount: promotion.threshold_amount?.toString() || '',
      start_date: new Date(promotion.start_date).toISOString().slice(0, 16),
      end_date: new Date(promotion.end_date).toISOString().slice(0, 16),
      is_active: promotion.is_active
    });
    setIsCreating(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this promotion?')) {
      return;
    }

    try {
      await GiftCardPromotionService.deletePromotion(id);
      toast.success('Promotion deleted');
      loadPromotions();
    } catch (error) {
      console.error('Error deleting promotion:', error);
      toast.error('Failed to delete promotion');
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    try {
      await GiftCardPromotionService.toggleActive(id, !currentStatus);
      toast.success(`Promotion ${!currentStatus ? 'activated' : 'deactivated'}`);
      loadPromotions();
    } catch (error) {
      console.error('Error toggling promotion:', error);
      toast.error('Failed to update promotion status');
    }
  };

  const isPromotionActive = (promotion: GiftCardPromotion) => {
    const now = new Date();
    const start = new Date(promotion.start_date);
    const end = new Date(promotion.end_date);
    return promotion.is_active && now >= start && now <= end;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen && !isAnimating) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
        onTransitionEnd={handleTransitionEnd}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className={`bg-white rounded-[20px] shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden pointer-events-auto transform transition-all duration-300 ${
            isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
          style={{
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <div>
              <h2 className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                Gift Card Promotions
              </h2>
              <p className="text-[15px] text-gray-600 mt-1" style={{ letterSpacing: '-0.01em' }}>
                Create bonus promotions to incentivize gift card purchases
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-[10px] transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {!isCreating ? (
              <>
                {/* Create Button */}
                <button
                  onClick={() => setIsCreating(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-[12px] text-[15px] font-semibold transition-colors mb-6"
                >
                  <Plus className="h-5 w-5" />
                  Create Promotion
                </button>

                {/* Promotions List */}
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-3 border-blue-600 border-t-transparent"></div>
                  </div>
                ) : promotions.length === 0 ? (
                  <div className="text-center py-12">
                    <Tag className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-[15px] text-gray-500">No promotions yet</p>
                    <p className="text-[13px] text-gray-400 mt-1">Create your first promotion to get started</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {promotions.map((promotion) => {
                      const isActive = isPromotionActive(promotion);
                      return (
                        <div
                          key={promotion.id}
                          className={`p-4 border rounded-[12px] transition-all ${
                            isActive
                              ? 'border-green-200 bg-green-50'
                              : promotion.is_active
                              ? 'border-gray-200 bg-white'
                              : 'border-gray-200 bg-gray-50 opacity-60'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="text-[15px] font-semibold text-gray-900">
                                  {promotion.name}
                                </h3>
                                {isActive && (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[11px] font-semibold rounded-full">
                                    ACTIVE NOW
                                  </span>
                                )}
                                {!promotion.is_active && (
                                  <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-[11px] font-semibold rounded-full">
                                    DISABLED
                                  </span>
                                )}
                              </div>
                              {promotion.description && (
                                <p className="text-[13px] text-gray-600 mb-3">{promotion.description}</p>
                              )}
                              <div className="flex flex-wrap items-center gap-4 text-[13px] text-gray-600">
                                <div className="flex items-center gap-1.5">
                                  {promotion.bonus_type === 'percentage' ? (
                                    <Percent className="h-4 w-4 text-blue-500" />
                                  ) : promotion.bonus_type === 'incremental' ? (
                                    <Tag className="h-4 w-4 text-purple-500" />
                                  ) : (
                                    <DollarSign className="h-4 w-4 text-green-500" />
                                  )}
                                  <span className="font-medium">
                                    {promotion.bonus_type === 'percentage'
                                      ? `${promotion.bonus_value}% bonus`
                                      : promotion.bonus_type === 'incremental'
                                      ? `$${promotion.bonus_value} per $${promotion.threshold_amount || 0}`
                                      : `$${promotion.bonus_value} bonus`}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Calendar className="h-4 w-4 text-gray-400" />
                                  <span>
                                    {formatDate(promotion.start_date)} - {formatDate(promotion.end_date)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <button
                                onClick={() => handleToggleActive(promotion.id, promotion.is_active)}
                                className="p-2 hover:bg-gray-100 rounded-[8px] transition-colors"
                                title={promotion.is_active ? 'Deactivate' : 'Activate'}
                              >
                                {promotion.is_active ? (
                                  <ToggleRight className="h-5 w-5 text-green-600" />
                                ) : (
                                  <ToggleLeft className="h-5 w-5 text-gray-400" />
                                )}
                              </button>
                              <button
                                onClick={() => handleEdit(promotion)}
                                className="p-2 hover:bg-gray-100 rounded-[8px] transition-colors"
                                title="Edit"
                              >
                                <Edit2 className="h-5 w-5 text-gray-600" />
                              </button>
                              <button
                                onClick={() => handleDelete(promotion.id)}
                                className="p-2 hover:bg-red-50 rounded-[8px] transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="h-5 w-5 text-red-600" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Create/Edit Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-[13px] font-semibold text-gray-700 mb-2">
                      Promotion Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Holiday Bonus 2025"
                      required
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[13px] font-semibold text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Optional description"
                      rows={2}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-[13px] font-semibold text-gray-700 mb-2">
                      Bonus Type *
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, bonus_type: 'percentage' })}
                        className={`flex flex-col items-center justify-center gap-2 px-3 py-3 rounded-[10px] border-2 transition-all ${
                          formData.bonus_type === 'percentage'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <Percent className="h-5 w-5" />
                        <span className="font-semibold text-sm">Percentage</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, bonus_type: 'fixed' })}
                        className={`flex flex-col items-center justify-center gap-2 px-3 py-3 rounded-[10px] border-2 transition-all ${
                          formData.bonus_type === 'fixed'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <DollarSign className="h-5 w-5" />
                        <span className="font-semibold text-sm">Fixed</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, bonus_type: 'incremental' })}
                        className={`flex flex-col items-center justify-center gap-2 px-3 py-3 rounded-[10px] border-2 transition-all ${
                          formData.bonus_type === 'incremental'
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <Tag className="h-5 w-5" />
                        <span className="font-semibold text-sm">Incremental</span>
                      </button>
                    </div>
                  </div>

                  {formData.bonus_type === 'incremental' ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[13px] font-semibold text-gray-700 mb-2">
                          Spending Threshold * 
                        </label>
                        <div className="relative">
                          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                            $
                          </div>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={formData.threshold_amount}
                            onChange={(e) => setFormData({ ...formData, threshold_amount: e.target.value })}
                            placeholder="e.g., 50.00"
                            required
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <p className="text-[11px] text-gray-500 mt-1.5">
                          Amount needed for each bonus
                        </p>
                      </div>
                      <div>
                        <label className="block text-[13px] font-semibold text-gray-700 mb-2">
                          Bonus per Threshold *
                        </label>
                        <div className="relative">
                          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                            $
                          </div>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            value={formData.bonus_value}
                            onChange={(e) => setFormData({ ...formData, bonus_value: e.target.value })}
                            placeholder="e.g., 40.00"
                            required
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <p className="text-[11px] text-gray-500 mt-1.5">
                          Bonus earned per threshold
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-2">
                        Bonus Value *
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                          {formData.bonus_type === 'percentage' ? '%' : '$'}
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={formData.bonus_value}
                          onChange={(e) => setFormData({ ...formData, bonus_value: e.target.value })}
                          placeholder={formData.bonus_type === 'percentage' ? 'e.g., 10' : 'e.g., 10.00'}
                          required
                          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1.5">
                        {formData.bonus_type === 'percentage'
                          ? 'E.g., 10 means customers get 10% bonus on their purchase'
                          : 'E.g., 10 means customers get $10 bonus per purchase'}
                      </p>
                    </div>
                  )}

                  {/* Example for Incremental */}
                  {formData.bonus_type === 'incremental' && formData.threshold_amount && formData.bonus_value && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-[10px]">
                      <p className="text-[12px] font-semibold text-blue-900 mb-2">Example:</p>
                      <div className="space-y-1 text-[11px] text-blue-800">
                        <p>• Spend ${formData.threshold_amount} → Get ${formData.bonus_value} bonus</p>
                        <p>• Spend ${(parseFloat(formData.threshold_amount) * 2).toFixed(2)} → Get ${(parseFloat(formData.bonus_value) * 2).toFixed(2)} bonus</p>
                        <p>• Spend ${(parseFloat(formData.threshold_amount) * 3).toFixed(2)} → Get ${(parseFloat(formData.bonus_value) * 3).toFixed(2)} bonus</p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-2">
                        Start Date & Time *
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        required
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-[13px] font-semibold text-gray-700 mb-2">
                        End Date & Time *
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                        required
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="is_active" className="text-[13px] text-gray-700">
                      Active (promotion will be applied during the date range)
                    </label>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setIsCreating(false);
                        setEditingPromotion(null);
                        resetForm();
                      }}
                      className="flex-1 px-4 py-2.5 border border-gray-200 rounded-[10px] text-[15px] font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-[10px] text-[15px] font-semibold transition-colors"
                    >
                      {editingPromotion ? 'Update Promotion' : 'Create Promotion'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

